import React from 'react'
import PropTypes from 'prop-types'
import { WingsBadge } from '../badges'
import { WingsQuoteText } from '../typography'
import WingsCardContainer from './WingsCardContainer'
import { useIntl } from 'react-intl'

/**
 * WingsStoryCard Component
 * 
 * A card component for displaying user stories with image, quote, and career information.
 * 
 * @component
 * @example
 * <WingsStoryCard
 *   img="https://example.com/image.jpg"
 *   title="Morgan Rupert"
 *   slug="morgan-rupert-story"
 *   storyJobTitle="Registered Nurse"
 *   storyCompany="Seminary Manor"
 *   storyQuote="My dad was diagnosed with brain cancer when I was a senior in high school..."
 *   objectID="123"
 *   contentType="stories"
 * />
 */
const WingsStoryCard = ({
  img,
  video,
  title,
  slug,
  storyJobTitle,
  storyCompany,
  storyQuote,
  objectID,
  contentType = 'stories',
  hideBookmark = false,
  className = '',
  onImageButtonClick,
}) => {
  const { formatMessage } = useIntl()
  const imageButtonLabel = onImageButtonClick
    ? formatMessage({ id: "career-plans-view-plan", defaultMessage: "View Plan" })
    : null

  return (
    <WingsCardContainer
      imageUrl={img ? `${img}?q=&auto=compress,&w=712&h=446&fit=crop&crop=faces` : "https://imgix.cosmicjs.com/e716b970-3899-11eb-9178-3f6d9415ab76-empty-blog-author.png"}
      imageAlt={`Image of ${title}`}
      videoUrl={video}
      to={`/story/${slug}`}
      title={storyJobTitle}
      subtitle={storyCompany}
      objectID={objectID}
      slug={slug}
      contentType={contentType}
      hideBookmark={hideBookmark}
      className={className}
      imageButtonLabel={imageButtonLabel}
      onImageButtonClick={onImageButtonClick}
    >
      {storyQuote && (
        <WingsQuoteText size="small" className="w-m-0 w-mb-1">
          &ldquo;{storyQuote}&rdquo;
        </WingsQuoteText>
      )}
      <WingsBadge prefix="— ">
        {title}
      </WingsBadge>
    </WingsCardContainer>
  )
}

WingsStoryCard.propTypes = {
  /** Image URL for the story card */
  img: PropTypes.string.isRequired,
  
  /** Name of the person */
  title: PropTypes.string.isRequired,
  
  /** URL slug for the story */
  slug: PropTypes.string.isRequired,
  
  /** Job title or career */
  storyJobTitle: PropTypes.string.isRequired,

  /** Company or school name */
  storyCompany: PropTypes.string,

  /** Quote from the person */
  storyQuote: PropTypes.string.isRequired,
  
  /** Unique identifier for bookmarking */
  objectID: PropTypes.string,
  
  /** Content type for bookmarking */
  contentType: PropTypes.string,
  
  /** Whether to hide the bookmark button */
  hideBookmarks: PropTypes.bool,
  
  /** Additional CSS classes */
  className: PropTypes.string,
}

export default WingsStoryCard